import { TWasmId, TWasmInteractionType } from "~/interfaces";
import { ICommonData, IStoreItem, IWasmStoreGeometry, IWasmStoreMarker } from "./store/interfaces";
import Wasm from "./wasm";
import WasmMapMarker from "./entities/wasm-map-marker";
import WasmMapGeometry from "./entities/wasm-map-geometry";
import { getRemovedIds, getWasmIntercationType } from "./utils";
import { watchMapContentChanges } from "~/state/map-content/utils";
import { store } from "~/state/store";
import WasmMapMarkerSharedPosition from "./entities/wasm-map-marker-shared-position";

interface IMappingItemMarker {
	storeId: IStoreItem["id"];
	type: "marker";
	marker: WasmMapMarker | WasmMapMarkerSharedPosition;
	data: IWasmStoreMarker;
	origInteractionType: ICommonData["interactionType"];
	wasmIds: Array<TWasmId>;
}

interface IMappingItemGeometry {
	storeId: IStoreItem["id"];
	type: "geometry";
	geometry: WasmMapGeometry;
	data: IWasmStoreGeometry;
	origInteractionType: ICommonData["interactionType"];
	wasmIds: Array<TWasmId>;
}

interface IWasmToStoreDataItem {
	storeId: IStoreItem["id"];
	wasmMarker?: IWasmStoreMarker;
	wasmGeometry?: IWasmStoreGeometry;
}

type TMappingItem = IMappingItemMarker | IMappingItemGeometry;

export default class WasmStoreMapping {
	#wasm: Wasm;

	#itemsMapping: {
		// klid data id => zmena + obj
		[key: IStoreItem["id"]]: TMappingItem;
	};

	// klic data id z wasm jako cislo -> data id str
	// wasm -> store, sdilene pro markery + geometrie
	#wasmToStoreMapping: {
		[key: TWasmId]: TMappingItem;
	};

	constructor(wasm: Wasm) {
		this.#wasm = wasm;
		this.#itemsMapping = {};
		this.#wasmToStoreMapping = {};

		this.#build();
	}

	/**
	 * Mapovani wasm entity id na store id
	 */
	getWasmToStoreData(wasmId: TWasmId): IWasmToStoreDataItem {
		if (wasmId in this.#wasmToStoreMapping) {
			const item = this.#wasmToStoreMapping[wasmId];

			return {
				storeId: item.storeId,
				wasmMarker: item.type === "marker"
					? item.data
					: null,
				wasmGeometry: item.type === "geometry"
					? item.data
					: null,
			};
		}

		return {
			storeId: 0,
		};
	}

	get stats() {
		const output = {
			// viditelnych/celkem...
			allStats: [0, 0],
			markersStats: [0, 0],
			markers: {},
			geometriesStats: [0, 0],
			geometries: {},
			popupsStats: [0, 0],
			popups: {},
		};

		const allItems = Object.values(this.#itemsMapping);

		for (const item of allItems) {
			if (item.type === "marker") {
				const type = item.data.type;
				let key = "markers";
				const isVisible = item.data.visible;

				if (type.includes("popup")) {
					key = "popups";
					output.popupsStats[1]++;

					if (isVisible) {
						output.popupsStats[0]++;
					}
				} else {
					output.markersStats[1]++;

					if (isVisible) {
						output.markersStats[0]++;
					}
				}

				if (!(type in output[key])) {
					output[key][type] = {
						count: 0,
						items: [],
					};
				}

				if (isVisible) {
					output.allStats[0]++;
				}

				output[key][type].count++;
				output[key][type].items.push(item.data);
			} else if (item.type === "geometry") {
				const type = item.data.type;

				if (!(type in output.geometries)) {
					output.geometries[type] = {
						count: 0,
						items: [],
					};
				}

				if (item.data.visible) {
					output.geometriesStats[0]++;
					output.allStats[0]++;
				}

				output.geometriesStats[1]++;
				output.geometries[type].count++;
				output.geometries[type].items.push(item.data);
			}

			output.allStats[1]++;
		}

		return output;
	}

	#build() {
		// init stav
		const curState = store.getState().mapContent;
		const initMarkers = curState.markers;
		const initGeometries = curState.geometries;

		if (initMarkers.length > 0) {
			this.#markersChange(initMarkers.map(item => item.id), initMarkers);
		}

		if (initGeometries.length > 0) {
			this.#geometriesChange(initGeometries.map(item => item.id), initGeometries);
		}

		// zmeny stavu
		watchMapContentChanges({
			store,
			onMarkersChange: (ids, markers) => {
				this.#markersChange(ids, markers);
			},
			onGeometriesChange: (ids, geometries) => {
				this.#geometriesChange(ids, geometries);
			},
		});
	}

	#addMarker(markerData: IWasmStoreMarker) {
		const wasmMarker = this.#wasm.createMarker(markerData);
		const wasmIds = wasmMarker.wasmIds;

		this.#itemsMapping[markerData.id] = {
			storeId: markerData.id,
			type: "marker",
			data: structuredClone(markerData),
			marker: wasmMarker,
			wasmIds,
			origInteractionType: markerData.interactionType,
		};

		for (const wasmId of wasmIds) {
			this.#wasmToStoreMapping[wasmId] = this.#itemsMapping[markerData.id];
		}
	}

	#removeItem(markerId: IStoreItem["id"]) {
		if (markerId in this.#itemsMapping) {
			const curData = this.#itemsMapping[markerId];
			let wasmIds: Array<TWasmId> = [];

			if (curData.type === "marker") {
				wasmIds = curData.marker.wasmIds;

				curData.marker.destroy();
			} else {
				wasmIds = curData.geometry.wasmIds;

				curData.geometry.destroy();
			}

			for (const wasmId of wasmIds) {
				delete this.#wasmToStoreMapping[wasmId];
			}

			delete this.#itemsMapping[markerId];
		}
	}

	#markersChange(affectedIds: Array<IStoreItem["id"]>, markers: Array<IWasmStoreMarker>) {
		const newIds: Array<IStoreItem["id"]> = [];

		// pridani + editace
		for (const marker of markers) {
			newIds.push(marker.id);

			// preskocime
			if (affectedIds.length > 0 && !affectedIds.includes(marker.id)) {
				continue;
			}

			if (marker.id in this.#itemsMapping) {
				const curData = this.#itemsMapping[marker.id] as IMappingItemMarker;

				if (curData.data.lastChange !== marker.lastChange) {
					if (curData.data.lon !== marker.lon || curData.data.lat !== marker.lat) {
						curData.marker.setPosition(marker.lon, marker.lat);
					}

					if (curData.data.visual !== marker.visual) {
						curData.marker.setVisual(marker.visual);
					}

					if (curData.data.visible !== marker.visible) {
						curData.marker.setVisibility(marker.visible);
					}

					if (curData.data.angle !== marker.angle) {
						curData.marker.setAngle(marker.angle);
					}

					if (curData.data.bringToTheFront !== marker.bringToTheFront) {
						curData.marker.setBringToTheFront(marker.bringToTheFront);
					}

					if (curData.data.interactionType !== marker.interactionType) {
						const interactionType: TWasmInteractionType = marker.interactionType === curData.origInteractionType
							? "none"
							: curData.origInteractionType;

						this.#suppresIds(curData.wasmIds, interactionType);
					}

					curData.data = structuredClone(marker);
				}
			} else {
				// neexistuje
				this.#addMarker(marker);
			}
		}

		// mazani
		const allIds = Object.keys(this.#itemsMapping).map(item => parseFloat(item)).filter(item => this.#itemsMapping[item].type === "marker");
		const removed = getRemovedIds(allIds, newIds);

		for (const removeId of removed) {
			this.#removeItem(removeId);
		}
	}

	#addGeometry(geomData: IWasmStoreGeometry) {
		const wasmGeometry = this.#wasm.createGeometry(geomData);
		const wasmIds = wasmGeometry.wasmIds;

		this.#itemsMapping[geomData.id] = {
			type: "geometry",
			storeId: geomData.id,
			data: structuredClone(geomData),
			geometry: wasmGeometry,
			wasmIds,
			origInteractionType: geomData.interactionType,
		};

		for (const wasmId of wasmIds) {
			this.#wasmToStoreMapping[wasmId] = this.#itemsMapping[geomData.id];
		}
	}

	#geometriesChange(affectedIds: Array<IStoreItem["id"]>, geometries: Array<IWasmStoreGeometry>) {
		const newIds: Array<IStoreItem["id"]> = [];

		// pridani + editace
		for (const geometry of geometries) {
			newIds.push(geometry.id);

			// preskocime
			if (affectedIds.length > 0 && !affectedIds.includes(geometry.id)) {
				continue;
			}

			if (geometry.id in this.#itemsMapping) {
				const curData = this.#itemsMapping[geometry.id] as IMappingItemGeometry;

				if (curData.data.lastChange !== geometry.lastChange) {
					if (curData.data.lastGeomChange !== geometry.lastGeomChange) {
						curData.geometry.updatePoints(geometry.points);
						curData.geometry.updateStrPoints(geometry.strPoints);
					}

					if (curData.data.visual !== geometry.visual) {
						curData.geometry.setVisual(geometry.visual);
					}

					if (curData.data.visible !== geometry.visible) {
						curData.geometry.setVisibility(geometry.visible);
					}

					if (curData.data.bringToTheFront !== geometry.bringToTheFront) {
						curData.geometry.setBringToTheFront(geometry.bringToTheFront);
					}

					if (curData.data.interactionType !== geometry.interactionType) {
						const interactionType: TWasmInteractionType = geometry.interactionType === curData.origInteractionType
							? "none"
							: curData.origInteractionType;

						this.#suppresIds(curData.wasmIds, interactionType);
					}

					// aktualizujeme
					curData.data = structuredClone(geometry);
				}
			} else {
				// neexistuje
				this.#addGeometry(geometry);
			}
		}

		// mazani
		const allIds = Object.keys(this.#itemsMapping).map(item => parseFloat(item)).filter(item => this.#itemsMapping[item].type === "geometry");
		const removed = getRemovedIds(allIds, newIds);

		for (const removeId of removed) {
			this.#removeItem(removeId);
		}
	}

	#suppresIds(wasmIds: Array<TWasmId>, interactionType: TWasmInteractionType) {
		this.#wasm.mapContent.suppressInteractionsFor(wasmIds, getWasmIntercationType(this.#wasm.module, interactionType));
	}
}
